import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { pushToast, useDispatch } from '../../../state';

const CROP_ASPECT = 1;
const CROP_AREA_SCALE = 0.9;

function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
}

export default function CompanyLogoUpload({
  value,
  pendingPreview,
  savedLogoUrl,
  logoLoading,
  onSavedLogoLoadError,
  onChange,
  onPendingLogoChange,
}: {
  value: string;
  pendingPreview: string | null;
  savedLogoUrl: string | null;
  logoLoading: boolean;
  onSavedLogoLoadError: () => void;
  onChange: (value: string) => void;
  onPendingLogoChange: (value: string | null) => void;
}) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [cropContainerSize, setCropContainerSize] = useState({
    width: 0,
    height: 0,
  });

  const displayUrl = pendingPreview ?? savedLogoUrl;
  const hasLogo = Boolean(displayUrl);

  useEffect(() => {
    if (!cropDialogOpen) return;

    const cropContainer = cropContainerRef.current;
    if (!cropContainer) return;

    const updateCropContainerSize = () => {
      const { width, height } = cropContainer.getBoundingClientRect();
      const nextSize = {
        width: Math.round(width),
        height: Math.round(height),
      };

      setCropContainerSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize,
      );
    };

    updateCropContainerSize();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateCropContainerSize);
    resizeObserver.observe(cropContainer);

    return () => resizeObserver.disconnect();
  }, [cropDialogOpen]);

  const cropSize =
    cropContainerSize.width > 0 && cropContainerSize.height > 0
      ? (() => {
          const availableWidth = cropContainerSize.width * CROP_AREA_SCALE;
          const availableHeight = cropContainerSize.height * CROP_AREA_SCALE;

          if (availableWidth / availableHeight > CROP_ASPECT) {
            return {
              width: Math.round(availableHeight * CROP_ASPECT),
              height: Math.round(availableHeight),
            };
          }

          return {
            width: Math.round(availableWidth),
            height: Math.round(availableWidth / CROP_ASPECT),
          };
        })()
      : undefined;

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      onPendingLogoChange(cropped);
      setCropDialogOpen(false);
      setImageSrc(null);
    } catch {
      dispatch(
        pushToast({
          message: 'Failed to crop company logo',
          type: 'error',
        }),
      );
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setImageSrc(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        Company Logo
      </Typography>

      {logoLoading ? (
        <Box
          sx={{
            width: 180,
            height: 180,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : hasLogo ? (
        <Box
          sx={{
            width: 180,
            height: 180,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <img
            src={displayUrl ?? undefined}
            alt="Company logo"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={onSavedLogoLoadError}
          />
        </Box>
      ) : (
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            width: 180,
            height: 180,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <CloudUploadIcon
            sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            Upload Image
          </Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelect}
      />

      {hasLogo && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Replace
        </Button>
      )}

      <Dialog
        open={cropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 1.5 }}>
          Crop Image
          <IconButton
            onClick={handleCropCancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          ref={cropContainerRef}
          dividers
          sx={{ p: 0, position: 'relative', height: 400 }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={CROP_ASPECT}
              cropSize={cropSize}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value as number)}
              sx={{ maxWidth: 200 }}
            />
          </Box>
          <Button onClick={handleCropCancel} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCropConfirm} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
